/**
 * Cliente de sesión httpOnly.
 *
 * El frontend NO maneja tokens de Cognito. Todo pasa por el backend proxy
 * /auth/* que setea cookies HttpOnly. Se usa el apiClient compartido
 * (withCredentials: true) para enviar/recibir esas cookies. Los endpoints
 * /auth/* están excluidos del interceptor global de apiClient (sin toast y
 * sin auto-refresh/redirect): cada pantalla muestra su propio error.
 */
import { isAxiosError, type AxiosError } from 'axios';

import { apiClient } from '../api/clienteHttp';
import type { AuthUser } from './types';

interface ApiErrorBody {
  error?: string;
}

/** Body no-objeto (vacío o no parseable) → {}, igual que el
 *  `res.json().catch(() => ({}))` de la versión con fetch. */
function normalizarBody<T>(data: unknown): T {
  return (data && typeof data === 'object' ? data : {}) as T;
}

/** AxiosError → Error plano con el MISMO mensaje que producía fetch:
 *  `data.error || 'Error {status}'`. Sin respuesta (fallo de red) se conserva
 *  el mensaje de transporte. Así el contrato de todos los callers
 *  (`e instanceof Error ? e.message : ...`) queda intacto. */
function normalizarError(err: unknown): Error {
  if (isAxiosError(err)) {
    const { response, message } = err as AxiosError<ApiErrorBody>;
    const status = response?.status;
    return new Error(response?.data?.error || (status ? `Error ${status}` : message));
  }
  return err instanceof Error ? err : new Error(String(err));
}

async function apiPost<T = unknown>(path: string, body: object): Promise<T> {
  try {
    const { data } = await apiClient.post(path, body);
    return normalizarBody<T>(data);
  } catch (err) {
    throw normalizarError(err);
  }
}

async function apiGet<T = unknown>(path: string): Promise<T> {
  try {
    const { data } = await apiClient.get(path);
    return normalizarBody<T>(data);
  } catch (err) {
    throw normalizarError(err);
  }
}

export interface PendingNewPassword {
  kind: 'new-password-required';
  session: string;
  email: string;
}

export type SignInOutcome =
  | { kind: 'session'; user: AuthUser }
  | PendingNewPassword;

export interface SessionCheckResult {
  isAuthenticated: boolean;
  hasRefreshToken: boolean;
  user: AuthUser | null;
  /**
   * `true` cuando /auth/session falló o devolvió una forma que no
   * reconocemos (backend todavía sin el contrato 200-siempre, red caída,
   * 5xx, HTML de un proxy/CDN) — a diferencia de un 200 confirmado con
   * `hasRefreshToken:false`, acá no sabemos si hay refresh_token.
   * AuthProvider puede igual intentar /auth/refresh en este caso, en vez
   * de asumir que no hay sesión recuperable.
   */
  isLegacyOrUnknown?: boolean;
}

export async function signIn(email: string, password: string): Promise<SignInOutcome> {
  const data = await apiPost<{ user?: AuthUser; challenge?: string; session?: string }>('/auth/login', {
    email: email.trim(),
    password,
  });

  if (data.challenge === 'NEW_PASSWORD_REQUIRED' && data.session) {
    return { kind: 'new-password-required', session: data.session, email };
  }

  if (!data.user) {
    throw new Error('Respuesta inesperada del servidor de autenticación');
  }

  return { kind: 'session', user: data.user };
}

export async function completeNewPassword(
  pending: PendingNewPassword,
  newPassword: string,
): Promise<AuthUser> {
  const data = await apiPost<{ user?: AuthUser }>('/auth/challenge', {
    email: pending.email,
    session: pending.session,
    newPassword,
  });

  if (!data.user) {
    throw new Error('No se pudo completar el cambio de contraseña');
  }

  return data.user;
}

export async function refreshSession(): Promise<AuthUser> {
  const data = await apiPost<{ user?: AuthUser }>('/auth/refresh', {});
  if (!data.user) {
    throw new Error('Sesión expirada');
  }
  return data.user;
}

/**
 * Consulta el estado de sesión (`GET /auth/session`), que siempre responde
 * HTTP 200 (nunca 401): `{ authenticated, hasRefreshToken?, user? }`. Se
 * normaliza el body a un resultado total sin opcionales, para que el
 * consumidor decida en un único lugar si debe intentar /auth/refresh.
 * Cualquier fallo de red, status no-200 o body no parseable degrada a
 * estado anónimo sin refresh token disponible.
 */
export async function getCurrentSession(): Promise<SessionCheckResult> {
  try {
    const data = await apiGet<{
      authenticated?: boolean;
      hasRefreshToken?: boolean;
      user?: AuthUser;
    }>('/auth/session');

    // Body sin el campo `authenticated` (backend en el contrato viejo que
    // solo mandaba `user`, o cualquier forma que no reconocemos) — no es un
    // 200 confirmado del contrato nuevo, tratarlo igual que una falla.
    if (typeof data.authenticated !== 'boolean') {
      return { isAuthenticated: false, hasRefreshToken: false, user: null, isLegacyOrUnknown: true };
    }

    const isAuthenticated = data.authenticated === true && !!data.user;
    return {
      isAuthenticated,
      hasRefreshToken: data.hasRefreshToken === true,
      user: isAuthenticated ? (data.user as AuthUser) : null,
    };
  } catch {
    return { isAuthenticated: false, hasRefreshToken: false, user: null, isLegacyOrUnknown: true };
  }
}

export async function signOut(): Promise<void> {
  try {
    await apiPost('/auth/logout', {});
  } catch {
    // Si falla el logout en el servidor, igual limpiamos estado local.
  }
}

export async function signUp(
  email: string,
  password: string,
  nombreEmpresa: string,
  rfc: string,
): Promise<void> {
  await apiPost('/auth/signup', {
    email: email.trim(),
    password,
    nombreEmpresa: nombreEmpresa.trim(),
    rfc: rfc.trim().toUpperCase(),
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await apiPost('/auth/confirm-signup', {
    email: email.trim(),
    code,
  });
}

export async function forgotPassword(email: string): Promise<void> {
  await apiPost('/auth/forgot-password', { email: email.trim() });
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await apiPost('/auth/confirm-forgot-password', {
    email: email.trim(),
    code,
    newPassword,
  });
}

/** Legacy: limpiar cualquier token que pudiera haber quedado en localStorage
 *  de builds anteriores. Con httpOnly no debería haber nada, pero mantenemos
 *  la limpieza por si acaso. */
try { localStorage.removeItem('a2c_asicontadores_session'); } catch { /* noop */ }
